import React, { useState } from "react";
import { motion } from "framer-motion";
import { Send, Handshake, Loader2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { CATALOG } from "@/data/catalog";
import { submitInquiry, submitPartnership } from "@/lib/api";
import { Reveal, MaskLines } from "./Reveal";

const inputClass =
  "w-full bg-transparent border-b border-white/20 py-3 text-white placeholder:text-zinc-600 focus:border-[#D4AF37] focus:outline-none transition-colors";
const labelClass = "block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1";

const Field = ({ label, opt, children }) => (
  <div>
    <label className={labelClass}>
      {label} {opt && <span className="text-zinc-700 normal-case tracking-normal">({opt})</span>}
    </label>
    {children}
  </div>
);

const SelectField = ({ label, value, onChange, options, placeholder, testid, opt }) => (
  <Field label={label} opt={opt}>
    <div className="relative">
      <select
        data-testid={testid}
        value={value}
        onChange={onChange}
        className={`${inputClass} appearance-none pr-8 ${value ? "text-white" : "text-zinc-600"}`}
      >
        <option value="" className="bg-[#0F1012]">{placeholder}</option>
        {options.map((o) => (
          <option key={o} value={o} className="bg-[#0F1012] text-white">{o}</option>
        ))}
      </select>
      <ChevronDown size={16} className="absolute right-1 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
    </div>
  </Field>
);

/* ---------------- Inquiry / RFQ ---------------- */
export const InquiryForm = () => {
  const { t, lang } = useLang();
  const f = t.inquiry;
  const [form, setForm] = useState({
    company: "", contact_name: "", email: "", phone: "", business_type: "", city: "", message: "",
  });
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const toggleCat = (name) =>
    setCats((p) => (p.includes(name) ? p.filter((c) => c !== name) : [...p, name]));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitInquiry({ ...form, categories: cats });
      toast.success(f.success);
      setForm({ company: "", contact_name: "", email: "", phone: "", business_type: "", city: "", message: "" });
      setCats([]);
    } catch (err) {
      toast.error(f.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="inquiry" data-testid="inquiry-section" className="relative py-24 sm:py-32">
      <div className="spot-glow w-[400px] h-[400px] bg-[#D4AF37]/10 top-20 left-0" />
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <Reveal>
            <p className="font-mono text-xs sm:text-sm uppercase tracking-[0.25em] text-[#D4AF37] mb-6">
              {f.overline}
            </p>
          </Reveal>
          <h2 className="font-display font-medium tracking-tighter text-4xl sm:text-5xl leading-[1.02] text-white">
            <MaskLines lines={[f.title]} />
          </h2>
          <Reveal delay={0.15}>
            <p className="mt-6 text-zinc-400 leading-relaxed">{f.lead}</p>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="lg:col-span-8">
          <form onSubmit={submit} data-testid="inquiry-form" className="rounded-2xl border border-white/10 bg-[#0F1012] p-6 sm:p-10">
            <div className="grid sm:grid-cols-2 gap-6">
              <Field label={f.fields.company}>
                <input data-testid="inq-company" required value={form.company} onChange={set("company")} className={inputClass} placeholder="—" />
              </Field>
              <Field label={f.fields.contact}>
                <input data-testid="inq-contact" required value={form.contact_name} onChange={set("contact_name")} className={inputClass} placeholder="—" />
              </Field>
              <Field label={f.fields.email}>
                <input data-testid="inq-email" required type="email" value={form.email} onChange={set("email")} className={inputClass} placeholder="—" />
              </Field>
              <Field label={f.fields.phone}>
                <input data-testid="inq-phone" required value={form.phone} onChange={set("phone")} className={inputClass} placeholder="—" />
              </Field>
              <SelectField testid="inq-business" label={f.fields.business} opt={t.common.optional} value={form.business_type} onChange={set("business_type")} options={f.businessOptions} placeholder={f.pick} />
              <Field label={f.fields.city} opt={t.common.optional}>
                <input data-testid="inq-city" value={form.city} onChange={set("city")} className={inputClass} placeholder="—" />
              </Field>
            </div>

            <div className="mt-8">
              <label className={labelClass}>{f.fields.categories}</label>
              <div className="flex flex-wrap gap-2 mt-3">
                {CATALOG.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    data-testid={`inq-cat-${c.id}`}
                    onClick={() => toggleCat(c.name[lang])}
                    className={`px-4 py-2 rounded-full text-sm border transition-all active:scale-95 ${
                      cats.includes(c.name[lang])
                        ? "bg-[#D4AF37] text-black border-[#D4AF37] font-semibold"
                        : "border-white/15 text-zinc-300 hover:border-white/40"
                    }`}
                  >
                    {c.name[lang]}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-8">
              <Field label={f.fields.message}>
                <textarea data-testid="inq-message" required rows={4} value={form.message} onChange={set("message")} className={`${inputClass} resize-none`} placeholder="—" />
              </Field>
            </div>

            <button
              type="submit"
              data-testid="inquiry-submit"
              disabled={loading}
              className="mt-8 group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#D4AF37] text-black font-bold hover:bg-[#F3CE5A] active:scale-95 transition-all disabled:opacity-60"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={17} />}
              {loading ? f.sending : f.submit}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

/* ---------------- Partnership ---------------- */
export const PartnershipForm = () => {
  const { t } = useLang();
  const p = t.partner;
  const [form, setForm] = useState({
    company: "", pib: "", contact_name: "", email: "", phone: "", business_type: "", city: "", monthly_volume: "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitPartnership(form);
      toast.success(p.success);
      setForm({ company: "", pib: "", contact_name: "", email: "", phone: "", business_type: "", city: "", monthly_volume: "", message: "" });
    } catch (err) {
      toast.error(p.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="partner" data-testid="partner-section" className="relative py-24 sm:py-32 bg-[#0A0A0B] border-y border-white/10">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-8 lg:order-2">
          <form onSubmit={submit} data-testid="partner-form" className="rounded-2xl border border-white/10 bg-[#0F1012] p-6 sm:p-10">
            <div className="grid sm:grid-cols-2 gap-6">
              <Field label={p.fields.company}>
                <input data-testid="pt-company" required value={form.company} onChange={set("company")} className={inputClass} placeholder="—" />
              </Field>
              <Field label={p.fields.pib} opt={t.common.optional}>
                <input data-testid="pt-pib" value={form.pib} onChange={set("pib")} className={inputClass} placeholder="—" />
              </Field>
              <Field label={p.fields.contact}>
                <input data-testid="pt-contact" required value={form.contact_name} onChange={set("contact_name")} className={inputClass} placeholder="—" />
              </Field>
              <Field label={p.fields.email}>
                <input data-testid="pt-email" required type="email" value={form.email} onChange={set("email")} className={inputClass} placeholder="—" />
              </Field>
              <Field label={p.fields.phone}>
                <input data-testid="pt-phone" required value={form.phone} onChange={set("phone")} className={inputClass} placeholder="—" />
              </Field>
              <Field label={p.fields.city} opt={t.common.optional}>
                <input data-testid="pt-city" value={form.city} onChange={set("city")} className={inputClass} placeholder="—" />
              </Field>
              <Field label={p.fields.business} opt={t.common.optional}>
                <input data-testid="pt-business" value={form.business_type} onChange={set("business_type")} className={inputClass} placeholder="—" />
              </Field>
              <Field label={p.fields.volume} opt={t.common.optional}>
                <input data-testid="pt-volume" value={form.monthly_volume} onChange={set("monthly_volume")} className={inputClass} placeholder="—" />
              </Field>
            </div>
            <div className="mt-8">
              <Field label={p.fields.message} opt={t.common.optional}>
                <textarea data-testid="pt-message" rows={3} value={form.message} onChange={set("message")} className={`${inputClass} resize-none`} placeholder="—" />
              </Field>
            </div>
            <button
              type="submit"
              data-testid="partner-submit"
              disabled={loading}
              className="mt-8 group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-[#2E8B57] bg-[#1E5631]/20 text-white font-bold hover:bg-[#1E5631]/40 active:scale-95 transition-all disabled:opacity-60"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Handshake size={17} />}
              {loading ? p.sending : p.submit}
            </button>
          </form>
        </Reveal>

        <div className="lg:col-span-4 lg:order-1 flex flex-col justify-center">
          <Reveal>
            <p className="font-mono text-xs sm:text-sm uppercase tracking-[0.25em] text-[#2E8B57] mb-6">
              {p.overline}
            </p>
          </Reveal>
          <h2 className="font-display font-medium tracking-tighter text-4xl sm:text-5xl leading-[1.02] text-white">
            <MaskLines lines={[p.title]} />
          </h2>
          <Reveal delay={0.15}>
            <p className="mt-6 text-zinc-400 leading-relaxed">{p.lead}</p>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
